import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useMenuDrawerContext } from "../../../../components/menu/MenuDrawerContextProvider.shared";
import { useOrderLinkFlowOrder } from "../../../../hooks/api/v2/orderLinkFlow/useOrderLinkFlowOrder";
import { OrderBanner } from "../deliveryPreferences/components/OrderBanner";
import { OrderSummaryDrawer } from "../deliveryPreferences/components/OrderSummaryDrawer";
import { BusinessUserAccessGuard } from "../guards/BusinessUserAccessGuard";
import { AddMoreToDeliveryStep } from "./steps/AddMoreToDeliveryStep";
import { ORDER_BUYER } from "../../../../constants/routes";
import { Icon } from "@sellar-io/components";
import { useEvents } from "../../../../hooks";
import { getSellarWebsiteUtms } from "../../../../utils/utmHelper";

type EditAddSelectionView = "selection" | "addMore";

interface OptionCardProps {
  title: string;
  description: string;
  onClick: () => void;
}

export function OptionCard({ title, description, onClick }: OptionCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full cursor-pointer items-center justify-between rounded-xl border border-zinc-200 bg-white p-4 text-left transition-all hover:border-zinc-400 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-zinc-800 focus-visible:outline-none"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-zinc-800">{title}</p>
        <p className="mt-0.5 text-xs text-zinc-500">{description}</p>
      </div>
      <div className="ml-3 shrink-0 text-zinc-400">
        <Icon icon="CHEVRON_RIGHT" size={0.9} />
      </div>
    </button>
  );
}

export const EditAddSelectionPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [currentView, setCurrentView] =
    useState<EditAddSelectionView>("selection");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const orderQuery = useOrderLinkFlowOrder(token);
  const { setDisplayTypeOverride } = useMenuDrawerContext();
  const { track } = useEvents();

  useEffect(() => {
    setDisplayTypeOverride("hidden");
    return () => setDisplayTypeOverride(undefined);
  }, [setDisplayTypeOverride]);

  useEffect(() => {
    track("Order Edit Add Selection Viewed", { ...getSellarWebsiteUtms() });
  }, [track]);

  const order = orderQuery.data;

  return (
    <BusinessUserAccessGuard token={token}>
      <div className="flex min-h-screen flex-col">
        {order && (
          <OrderBanner
            order={order}
            onShowDetails={() => setDrawerOpen(true)}
          />
        )}

        {currentView === "selection" && (
          <div className="w-full flex-1 border-t border-zinc-200 bg-neutral-100 px-5 py-8">
            <div className="mx-auto w-full max-w-md">
              <div className="mb-5">
                <h2 className="text-xl font-semibold text-zinc-800">
                  How do you want to edit or add to this order?
                </h2>
                <p className="mt-1 text-sm text-zinc-500">
                  You can choose to edit your existing{" "}
                  {order?.supplierName ?? "supplier"} order, or add items from
                  different suppliers
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <OptionCard
                  title={`Edit/ add to ${order?.supplierName ?? "supplier"} order`}
                  description={`Adjust the items or quantities in your ${order?.supplierName ?? "supplier"} order`}
                  onClick={() =>
                    navigate(
                      `${ORDER_BUYER.href({ supplierIdOrUrlId: order!.supplierUrlId })}?orderFlowToken=${encodeURIComponent(token!)}`,
                      { replace: true },
                    )
                  }
                />
                <OptionCard
                  title="Add items from different suppliers"
                  description={`Your added products will arrive together with your ${order?.supplierName ?? "supplier"} order`}
                  onClick={() => setCurrentView("addMore")}
                />
              </div>
            </div>
          </div>
        )}

        {currentView === "addMore" && (
          <AddMoreToDeliveryStep
            buyerPostcode={order?.retailerPostalCode ?? null}
            excludeSupplierId={order?.supplierId ?? null}
            onBack={() => setCurrentView("selection")}
          />
        )}

        {order && (
          <OrderSummaryDrawer
            active={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            order={order}
          />
        )}
      </div>
    </BusinessUserAccessGuard>
  );
};
